import apiClient from './client';
import type { ApiResponse, LoginResponse, User, SwitchTenantResponse } from '@/types';

/** 发起注册：发送邮箱验证链接（支持邀请码 + UTM 渠道归因） */
export async function registerInitiate(data: {
  companyName: string;
  email: string;
  password: string;
  displayName: string;
  phone?: string;
  /** 邀请码（来自分享链接 ?invite=CODE） */
  invite?: string;
  /** 企业成员邀请码（来自 ?join=J-XXXXXXXX），带此码则加入既有企业而非新建 */
  tenantInvite?: string;
  /** 注册时选择的身份：owner/executive/consultant/other */
  identity?: string;
  /** UTM 渠道参数（来自分享链接 utm_*） */
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmTerm?: string;
  utmContent?: string;
}): Promise<ApiResponse<{ message: string; debug?: { verifyUrl: string } }>> {
  const res = await apiClient.post('/auth/register-initiate', data, {
    headers: { 'X-Client-Base-Url': window.location.origin },
  });
  return res.data;
}

/** 点击邮箱验证链接，完成注册 */
export async function verifyEmailAndRegister(token: string): Promise<ApiResponse<{ verified: boolean; message: string }>> {
  const res = await apiClient.get(`/auth/verify-email/${token}`);
  return res.data;
}

/** 登录 */
export async function login(data: {
  email?: string;
  phone?: string;
  password: string;
}): Promise<ApiResponse<LoginResponse>> {
  const res = await apiClient.post('/auth/login', data);
  return res.data;
}

/** 刷新 Token */
export async function refreshToken(token: string): Promise<ApiResponse<{ token: string }>> {
  const res = await apiClient.post('/auth/refresh', { token });
  return res.data;
}

/** 获取当前用户信息 */
export async function getMe(): Promise<ApiResponse<{ user: User; currentTenant: string | null; isPlatformAdmin?: boolean; isChannel?: boolean; consultantStatus?: 'active' | 'pending' | 'disabled' | null; certStatus?: 'approved' | 'pending' | 'rejected' | null }>> {
  const res = await apiClient.get('/auth/me');
  return res.data;
}

/** 发送密码重置链接 */
export async function forgotPassword(email: string): Promise<ApiResponse<{ message: string; debug?: { resetUrl: string } }>> {
  const res = await apiClient.post('/auth/forgot-password', { email }, {
    headers: { 'X-Client-Base-Url': window.location.origin },
  });
  return res.data;
}

/** 重置密码 */
export async function resetPassword(token: string, password: string): Promise<ApiResponse<{ message: string }>> {
  const res = await apiClient.post(`/auth/reset-password/${token}`, { password });
  return res.data;
}

/** 微信占位账号升级为正式企业：原地升级或返回「加入已有企业」提示 */
export interface UpgradeWechatTenantResponse {
  /** upgraded=原地升级成功；join=手机号/邮箱命中已有真实企业，需引导加入 */
  action: 'upgraded' | 'join';
  /** action=upgraded 时返回：升级后的用户（isWechatPlaceholder=false） */
  user?: User;
  /** action=upgraded 时返回：升级后的租户 */
  tenant?: { id: string; name: string };
  /** action=upgraded 时返回：重签发的 JWT */
  token?: string;
  /** action=join 时返回：命中的已有真实企业 */
  existingTenant?: { id: string; name: string };
}

/** 微信占位账号「加入/切换」到已有真实企业 */
export interface JoinExistingTenantResponse {
  action: 'joined';
  user: User;
  tenant: { id: string; name: string };
  token: string;
}

/** 微信占位账号提交企业信息：默认原地升级为正式企业；若手机号/邮箱命中已有真实账号，则返回 action:'join' 引导加入 */
export async function upgradeWechatTenant(data: {
  companyName: string;
  phone: string;
  password: string;
  email: string;
  displayName?: string;
}): Promise<ApiResponse<UpgradeWechatTenantResponse>> {
  const res = await apiClient.post('/auth/upgrade-wechat-tenant', data);
  return res.data;
}

/** 微信占位账号加入/切换到已有真实企业（action:'join' 分支的二次确认后调用） */
export async function joinExistingTenant(data: {
  targetTenantId: string;
  displayName?: string;
}): Promise<ApiResponse<JoinExistingTenantResponse>> {
  const res = await apiClient.post('/auth/join-tenant', data);
  return res.data;
}

/** 当前用户自助改身份：更新身份并重新签发 token（即时生效咨询师权限语义） */
export async function changeIdentity(data: {
  identity: string;
}): Promise<ApiResponse<{
  identity: string | null;
  consultantStatus: 'active' | 'pending' | 'disabled' | null;
  token: string;
  isConsultant: boolean;
  isPlatformAdmin: boolean;
}>> {
  const res = await apiClient.patch('/auth/me/identity', data);
  return res.data;
}

/** 个人中心：当前用户自助更新基本资料（昵称/头像/简介/城市） */
export async function updateProfile(data: {
  displayName?: string;
  avatar?: string | null;
  bio?: string;
  city?: string | null;
}): Promise<ApiResponse<{ user: User }>> {
  const res = await apiClient.patch('/auth/me', data);
  return res.data;
}
